// Web adapter over @famanage/i18n — the shared translation table + runtime.
//
// This file used to carry its own 3,700-line fork of the translation table;
// that fork was merged into packages/i18n (2026-08-06) and the web now keeps
// NO local copy. The adapter below only wires the web platform in:
//   - localStorage as the language store (same 'fm.lang' key as before),
//   - the <html lang/dir> side effect on every language change,
// and re-exports the same public surface web code always imported from here:
// t, getLang, setLang, isRtl, Lang, dateLocale, formatDateTime,
// notificationTitle, pickTranslation, bootstrapLang.
//
// Usage: t('Home') → 'Home' (en) | 'הבית' (he) | 'گھر' (ur) | 'Bahay' (tl)

import {
  type ReadyLang,
  coerceLang,
  initI18n,
  isReadyLang,
  getLang as sharedGetLang,
  setLang as sharedSetLang,
} from '@famanage/i18n';

export {
  bootstrapLang,
  dateLocale,
  formatDateTime,
  isRtl,
  notificationTitle,
  pickTranslation,
  t,
} from '@famanage/i18n';

/**
 * The web offers exactly the READY languages (full dictionary coverage,
 * accepted by the server's preferred_lang enum) — same four codes this file
 * hardcoded before the merge. Anything else in storage coerces to English.
 */
export type Lang = ReadyLang;

const LANG_KEY = 'fm.lang'; // the same key the pre-merge web build wrote

// Boot, synchronously at import time (localStorage is synchronous, so the
// async hydration inside initI18n is redundant here — the explicit setLang
// below installs the stored language before anything renders, exactly like
// the old implementation's live localStorage reads did). getItem coerces so
// a foreign code written by a newer/mobile build can never leak past the
// four ready languages, matching the old validation in getLang.
void initI18n({
  storage: {
    getItem: (k) => coerceLang(localStorage.getItem(k)),
    setItem: (k, v) => localStorage.setItem(k, v),
    removeItem: (k) => localStorage.removeItem(k),
  },
  onLangChange(lang, dir) {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  },
});
void sharedSetLang(coerceLang(localStorage.getItem(LANG_KEY)));

export function getLang(): Lang {
  const l = sharedGetLang();
  return isReadyLang(l) ? l : 'en';
}

export function setLang(l: Lang): void {
  void sharedSetLang(l);
}
